import { ConvexHttpClient } from "convex/browser";
import { CONVEX_URL } from "$lib/env";
import { api } from "@backend/convex/_generated/api";

/**
 * Minimal Svelte 5 adapter for `@convex-dev/auth`.
 *
 * `convex-svelte` handles querying and `setupAuth`, but it does not ship a
 * Convex Auth token manager. This ports the essential parts of the React
 * provider: token storage, `signIn`/`signOut`, and refresh-token rotation.
 */

const VERIFIER_KEY = "__convexAuthOAuthVerifier";
const JWT_KEY = "__convexAuthJWT";
const REFRESH_KEY = "__convexAuthRefreshToken";

interface Tokens {
  token: string;
  refreshToken: string;
}

interface SignInResult {
  redirect?: string;
  verifier?: string;
  tokens?: Tokens | null;
}

function storage(): Storage | null {
  return typeof localStorage === "undefined" ? null : localStorage;
}

class ConvexAuth {
  token = $state<string | null>(null);
  refreshToken = $state<string | null>(null);
  isLoading = $state(true);

  private readonly http: ConvexHttpClient;

  constructor(url: string) {
    this.http = new ConvexHttpClient(url);
  }

  get isAuthenticated(): boolean {
    return this.token !== null;
  }

  private persist(tokens: Tokens | null): void {
    const store = storage();
    if (!store) return;
    if (tokens) {
      store.setItem(JWT_KEY, tokens.token);
      store.setItem(REFRESH_KEY, tokens.refreshToken);
    } else {
      store.removeItem(JWT_KEY);
      store.removeItem(REFRESH_KEY);
    }
  }

  private async setTokens(tokens: Tokens | null): Promise<void> {
    this.token = tokens?.token ?? null;
    this.refreshToken = tokens?.refreshToken ?? null;
    this.persist(tokens);
    this.isLoading = false;
  }

  /** Load stored tokens and complete an OAuth redirect if `?code=` is present. */
  async init(): Promise<void> {
    const store = storage();
    if (!store) {
      this.isLoading = false;
      return;
    }

    this.token = store.getItem(JWT_KEY);
    this.refreshToken = store.getItem(REFRESH_KEY);

    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    if (code) {
      url.searchParams.delete("code");
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
      try {
        await this.signIn(undefined, { code });
      } catch (error) {
        console.error("[auth] OAuth callback failed", error);
      }
    }

    this.isLoading = false;
  }

  /** Sign in with a provider (e.g. "discord", "password") or an OAuth code. */
  async signIn(provider?: string, params?: Record<string, unknown>): Promise<void> {
    const store = storage();
    const verifier = store?.getItem(VERIFIER_KEY) ?? undefined;
    store?.removeItem(VERIFIER_KEY);

    const result = (await this.http.action(api.auth.signIn as never, {
      provider,
      params: params ?? {},
      verifier,
    } as never)) as SignInResult;

    if (result.redirect !== undefined) {
      if (result.verifier) store?.setItem(VERIFIER_KEY, result.verifier);
      window.location.href = result.redirect;
      return;
    }
    if (result.tokens !== undefined) {
      await this.setTokens(result.tokens ?? null);
    }
  }

  async signOut(): Promise<void> {
    try {
      await this.http.action(api.auth.signOut as never, {} as never);
    } catch {
      // Ignore: the session may already be gone.
    }
    await this.setTokens(null);
  }

  /** Passed to `setupAuth`; rotates the refresh token when Convex asks. */
  async fetchAccessToken(args: { forceRefreshToken: boolean }): Promise<string | null> {
    if (args.forceRefreshToken && this.refreshToken) {
      try {
        const result = (await this.http.action(api.auth.signIn as never, {
          refreshToken: this.refreshToken,
        } as never)) as SignInResult;
        await this.setTokens(result.tokens ?? null);
      } catch {
        await this.setTokens(null);
        return null;
      }
    }
    return this.token;
  }
}

export const auth = new ConvexAuth(CONVEX_URL);
